import {
  DEFAULT_RANKING_WEIGHTS,
  RANKING_RULES,
  RANKING_WEIGHT_LABELS,
  rankingWeightsSchema,
  type RankingWeights,
} from '@remote-medium/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, ErrorState, LoadingState, cn, unwrap, useApi } from '@remote-medium/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { NumberRow } from './components/NumberRow.tsx';
import { WorkflowSettings } from './components/WorkflowSettings.tsx';
import { admin } from '../../lib/client.ts';
import { Card, Notice, Page, PageHeader, SectionHeader } from '../../lib/ui.tsx';

// Rules that add up to the 100-point base score; the availability bonus sits
// on top of it (PRODUCT.md "Ranking").
const BASE_RULES = RANKING_RULES.filter((r) => r !== 'availabilityBonus');

export function RankingPage() {
  const { data, error, loading } = useApi(() => unwrap(admin['ranking-config'].$get()), []);
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const { register, handleSubmit, reset, watch, formState } = useForm<RankingWeights>({
    resolver: zodResolver(rankingWeightsSchema),
    defaultValues: DEFAULT_RANKING_WEIGHTS,
  });
  const errors = formState.errors;

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  const w = watch();
  const total = BASE_RULES.reduce((n, r) => n + (Number(w[r]) || 0), 0);

  const save = handleSubmit(async (values) => {
    setMsg(null);
    try {
      reset(await unwrap(admin['ranking-config'].$put({ json: values })));
      setMsg({
        tone: 'info',
        text: 'Weights saved. Existing matches keep their scores until the role is re-ranked or the nightly run.',
      });
    } catch {
      setMsg({ tone: 'error', text: 'Could not save the weights.' });
    }
  });

  const row = (k: keyof RankingWeights, unit: string, hint?: string) => (
    <NumberRow
      key={k}
      id={`w-${k}`}
      label={RANKING_WEIGHT_LABELS[k]}
      hint={hint}
      unit={unit}
      error={errors[k]?.message}
      {...register(k, { setValueAs: Number })}
    />
  );

  return (
    <Page>
      <PageHeader
        title="Ranking"
        description="Deterministic and explainable. Each rule adds up to its weight, and every match shows exactly why. Region and employment type are hard filters applied before scoring, so they have no weight."
      />

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <form onSubmit={save} className="flex flex-col gap-6">
          <Card>
            <SectionHeader
              title="Rule weights"
              aside={
                <span className={cn(total !== 100 && 'text-foreground')}>
                  Base total <span className="font-medium tabular-nums">{total}</span> / 100
                </span>
              }
            />
            <div className="flex flex-col">{BASE_RULES.map((k) => row(k, 'pts'))}</div>
          </Card>

          <Card>
            <SectionHeader title="Bonus and thresholds" />
            <div className="flex flex-col">
              {row('availabilityBonus', 'pts', 'Added on top of the base score.')}
              {row('minScore', 'pts', 'Matches below this are never written.')}
              {row('workdayHours', 'hours', 'Used by the timezone overlap rule.')}
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => reset(DEFAULT_RANKING_WEIGHTS)}>
              Reset to defaults
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || !formState.isDirty}>
              {formState.isSubmitting ? 'Saving…' : 'Save weights'}
            </Button>
          </div>
        </form>

        <WorkflowSettings />
      </div>
    </Page>
  );
}
